import { EventEmitter, Inject, Injectable } from "@angular/core";
import { QUERY_SERVICE } from '../app.tokens';
import { TaxonomyStatus } from '../enums/taxonomy-status.enum';
import { IQueryService } from '../interfaces/query.service';
import { Client } from "../models/clients/client.model";
import { DataCell } from '../models/excel/cells/data-cell.model';
import { HeaderCell } from "../models/excel/cells/header-cell.model";
import { Field } from "../models/excel/entities/field.model";
import { ChildDropDownItem } from '../models/fields/child-dropdown-item.model';
import { Taxonomy } from "../models/taxonomies/taxonomy.model";
import { FieldType } from './../enums/field-type.enum';
import { AppHelper } from './../helpers/app.helper';
import { CellCascadingCache } from './../models/excel/cache/cascading/cell-cascading-cache.model';
import { Range } from './../models/excel/entities/range.model';
import { TaxonomyRow } from './../models/excel/taxonomies/taxonomy-row.mode';
import { DropDownItem } from './../models/fields/dropdown-item.model';
import { TaxonomyField } from './../models/fields/taxonomy-field.model';
import { ExcelExecutor } from './excel-executor.service';
import { ExcelService } from './excel.service';
import { LoggerService } from './logger.service';


@Injectable({ providedIn: 'root' })
export class TaxonomyService {
    //#region Fields

    private static readonly defaultTaxonomyTableRowCount = 255;
    private static readonly taxonomyTableNamePrefix = 'Taxonomy_Table_';
    private static readonly taxonomyValueIdStub = '{TaxonomyValueID}';
    private static readonly taxonomyTableNameRegExp = new RegExp('^' + TaxonomyService.taxonomyTableNamePrefix + '(\\d+)$');

    private processedKeys: { [taxonomyId: number]: { taxonomyValueColumnIndex: number, lastKey: string; }; } = {};
    private excelService: ExcelService;

    onDeleting = new EventEmitter<number>();
    onDeleted = new EventEmitter<number>();

    //#endregion

    //#region Properties

    get isInitialized(): boolean {
        return this.excelService !== undefined;
    }

    //#endregion

    //#region C-tor

    constructor(
        @Inject(QUERY_SERVICE) private queryService: IQueryService,
        private appHelper: AppHelper,
        private excelExecutor: ExcelExecutor,
        private logger: LoggerService) { }

    //#endregion

    //#region Initialization

    initializeAsync(): Promise<void> {
        return this.excelExecutor.run(async context => {
            const taxonomyTables = await this.getTaxonomyTablesAsync(context);
            const taxonomyIds = taxonomyTables.map(t => +TaxonomyService.taxonomyTableNameRegExp.exec(t.name)[1]);
            const excelService = new ExcelService(this.queryService, this.appHelper, this.logger);

            await excelService.initializeAsync(context, taxonomyIds);
            this.registerTableEventHandlers(context.workbook, taxonomyTables, taxonomyIds);
            await context.sync();

            this.excelService = excelService;
        });
    }

    private async getTaxonomyTablesAsync(context: Excel.RequestContext): Promise<Excel.Table[]> {
        const tablesCollection = context.workbook.tables;

        tablesCollection.load(['count']);
        await context.sync();

        let tables: Excel.Table[] = [];

        for (let i = 0; i < tablesCollection.count; i++) {
            tables.push(tablesCollection.getItemAt(i).load(['name']));
        }

        await context.sync();
        return tables.filter(t => TaxonomyService.taxonomyTableNameRegExp.test(t.name));
    }

    private registerTableEventHandlers(workbook: Excel.Workbook, tables: Excel.Table[], taxonomyIds: number[]): void {
        workbook.tables.onDeleted.add(async e => await this.onTableDelete(e));

        for (let i = 0; i < tables.length; i++) {
            const table = tables[i];
            const taxonomyId = taxonomyIds[i];

            table.onChanged.add(async e => await this.onTableChange(e, taxonomyId));
        }
    }

    //#endregion

    //#region On table delete

    private async onTableDelete(e: Excel.TableDeletedEventArgs): Promise<void> {
        this.logger.debug('Table delete detected.');

        const result = TaxonomyService.taxonomyTableNameRegExp.exec(e.tableName);

        if (result) {
            const taxonomyId = +result[1];

            this.onDeleting.emit(taxonomyId);

            await this.excelExecutor.run(async (context) => {
                await this.excelService.onTableRemoved(context.workbook, taxonomyId);
                delete this.processedKeys[taxonomyId];
            }).catch(error => this.appHelper.processExcelError(error));

            this.onDeleted.emit(taxonomyId);
        }
    }

    //#endregion

    //#region On table change

    private onTableChange(e: Excel.TableChangedEventArgs, taxonomyId: number): Promise<void> {
        return this.excelExecutor.run(async (context) => {
            if (e.changeType !== Excel.DataChangeType.rowDeleted &&
                e.changeType !== Excel.DataChangeType.columnInserted) {
                this.logger.debug('Table change detected.');

                let useShortcut = false;
                const range = Range.parse(e.address);
                const cached = this.processedKeys[taxonomyId];

                if (cached) {
                    if (cached.lastKey === range.toString()) {
                        useShortcut = true;
                        cached.lastKey = undefined;
                    } else if (range.columnIndex >= cached.taxonomyValueColumnIndex) {
                        useShortcut = true;
                    }
                }

                if (!useShortcut) {
                    const sheet = context.workbook.worksheets.getItem(e.worksheetId);
                    const table = sheet.tables.getItem(e.tableId);
                    const exRange = sheet.getRange(e.address);
                    const rows = await this.excelService.getRowsAsync(table, exRange, taxonomyId);

                    if (rows) {
                        const rowsToProcess = rows.filter(r => r.taxonomyValue.lastStatus !== TaxonomyStatus.Submitted);

                        if (rowsToProcess.length > 0) {
                            await this.excelService.downloadDataAsync(sheet, rows, taxonomyId);
                            await this.excelService.validateDataAsync(sheet, rowsToProcess);
                            await this.excelService.validateDropDownItemsAsync(table, rowsToProcess, taxonomyId);

                            rowsToProcess.forEach(r => {
                                this.applyCascading(r);
                                this.applyTaxonomyValue(r);
                            });

                            this.setChanges(sheet, taxonomyId, rowsToProcess);

                            await context.sync();
                        }
                    }
                }
            }
        }).catch(error => this.appHelper.processExcelError(error));
    }

    //#endregion

    //#region Insert

    insertDataAsync(taxonomyId: number, fields: TaxonomyField[]): Promise<void> {
    // Run everything inside excelExecutor.run so all Excel JS calls share a RequestContext
    return this.excelExecutor.run(async (context) => {
        this.logger.debug(`--- Starting insertDataAsync for Taxonomy: ${taxonomyId} ---`);

        // 1. PERFORMANCE BOOST: Suspend UI and Calculation
        // This prevents Excel from trying to redraw the screen/re-calc while we loop
        context.workbook.application.suspendScreenUpdatingUntilNextSync();
        context.workbook.application.calculationMode = Excel.CalculationMode.manual;

        try {
            // Get the currently selected range
            const range = context.workbook.getSelectedRange();
            range.load(['rowIndex', 'columnIndex']);
            
            // Initial sync to get coordinates
            await context.sync();

            const rowCount = TaxonomyService.defaultTaxonomyTableRowCount;
            const headerCells = this.getHeaderCells(fields);
            const dataCells = this.getDataCells(fields);
            const hiddenFields = this.getHiddenFields(fields);

            const tableRange = range.worksheet.getRangeByIndexes(
                range.rowIndex,
                range.columnIndex,
                rowCount + 1, 
                headerCells.length
            );

            // Overlap check
            const tables = tableRange.getTables();
            const count = tables.getCount();
            await context.sync();

            if (count.value > 0) {
                throw new Error('A table cannot overlap another table.');
            }

            // Create the table
            const table = this.excelService.createTable(
                tableRange,
                TaxonomyService.taxonomyTableNamePrefix + taxonomyId,
                taxonomyId,
                fields.map(f => f.id)
            );

            // Setup headers
            this.excelService.setupHeaders(table, headerCells);

            // 2. SETUP DATA (The heavy lifting happens here)
            // This now includes our Micro-Batching and logging
            await this.excelService.setupData(table, taxonomyId, dataCells, rowCount);

            // Setup hidden fields
            this.excelService.setupHiddenFields(table, hiddenFields);

            // Register change handler
            table.onChanged.add(async e => await this.onTableChange(e, taxonomyId));

            // Final sync for the main operations
            await context.sync();
            this.logger.debug(`Successfully inserted taxonomy Id: '${taxonomyId}'.`);

        } catch (error) {
            this.logger.error(`Error during insertDataAsync: ${error.message}`);
            throw error; // Re-throw so the UI can show the error
        } finally {
            // 3. RESTORE PERFORMANCE SETTINGS
            // This block runs even if an error occurred above.
            this.logger.debug("Restoring Excel calculation mode to automatic...");
            context.workbook.application.calculationMode = Excel.CalculationMode.automatic;
            
            // Final sync to ensure all changes and the mode restoration are applied
            await context.sync();
        }
    });
}

    private getHeaderCells(fields: TaxonomyField[]): HeaderCell[] {
        return fields
            .filter(f => f.field_type !== FieldType.TaxonomyId)
            .map(f => {
                const name = f.field_type === FieldType.Text
                    ? f.name + ' (' + f.character_limit + ')'
                    : f.name;

                const width = f.dropdown_items?.length > 0
                    ? f.dropdown_items
                        .map(i => i.name.length)
                        .reduce((one, two) => Math.max(one, two))
                    : f.character_limit;

                return new HeaderCell(f.id, name, width, f.field_desc);
            }).concat([
                new HeaderCell(undefined, 'Taxonomy value', 30, 'Contains auto generated taxonomy values'),
                new HeaderCell(undefined, 'Status', 30, 'Contains auto generated statuses')
            ]);
    }

    private getDataCells(fields: TaxonomyField[]): DataCell[] {
        return fields
            .filter(f => f.field_type !== FieldType.TaxonomyId)
            .map(f => {
                return new DataCell(
                    new Field(f.id, f.taxonomy_id, f.field_type,
                        f.name, f.hidden, f.mandatory, f.character_limit,
                        f.delimiter_value, f.dateFormat, f.mdm_entity, f.static_field_value, f.displayCode),
                    f.dropdown_items
                );
            }).concat([
                new DataCell(new Field(undefined, undefined, FieldType.Text)),
                new DataCell(new Field(undefined, undefined, FieldType.Text))
            ]);
    }

    private getHiddenFields(fields: TaxonomyField[]): Field[] {
        return fields
            .filter(f => f.field_type === FieldType.TaxonomyId)
            .map(f => new Field(f.id, f.taxonomy_id, f.field_type,
                f.name, f.hidden, f.mandatory, f.character_limit,
                f.delimiter_value, f.dateFormat, f.mdm_entity, f.static_field_value, f.displayCode));
    }

    //#endregion

    //#region Submit

    submitValuesAsync(clientId: number, taxonomyId: number): Promise<void> {
        return this.excelExecutor.run(async (context) => {
            this.logger.debug(`Submitting taxonomy values.. Id: '${taxonomyId}'.`);

            const table = context.workbook.tables.getItem(TaxonomyService.taxonomyTableNamePrefix + taxonomyId);
            const sheet = table.worksheet;
            const rows = await this.excelService.getRowsAsync(table, table.getDataBodyRange(), taxonomyId);

            if (rows) {
                const notEmptyRows = rows.filter(r => r.cells.some(c => c.value));
                let rowsToProcess = notEmptyRows.filter(r => r.taxonomyValue.lastStatus !== TaxonomyStatus.Submitted);

                await this.excelService.validateDataAsync(sheet, rowsToProcess);
                await this.excelService.validateDropDownItemsAsync(table, rowsToProcess, taxonomyId);

                notEmptyRows.forEach(r => {
                    if (r.taxonomyValue.lastStatus !== TaxonomyStatus.Submitted) {
                        this.applyCascading(r);
                    }

                    this.applyTaxonomyValue(r);
                });

                rowsToProcess = rowsToProcess.filter(r => r.taxonomyValue.status.value === TaxonomyStatus.New);

                if (rowsToProcess.length > 0) {
                    const taxonomyGroups = this.groupBy(notEmptyRows.map(r => r.taxonomyValue), 'value');

                    for (let i = 0; i < rowsToProcess.length; i++) {
                        const row = rowsToProcess[i];
                        const taxonomyValue = row.taxonomyValue;
                        const isSubmitted = taxonomyGroups[taxonomyValue.value].some(v =>
                            v.lastStatus === TaxonomyStatus.Submitted ||
                            v.status.value === TaxonomyStatus.Submitted);

                        if (isSubmitted) {
                            taxonomyValue.setStatus(TaxonomyStatus.Warning, 'Duplicate');
                        } else {
                            try {
                                const submitResult = await this.queryService.submitTaxonomyValue(clientId, taxonomyId, taxonomyValue).toPromise();

                                if (submitResult?.errorMessage) {
                                    taxonomyValue.setStatus(TaxonomyStatus.SubmittingError, submitResult.errorMessage);
                                }
                                else if (submitResult?.taxonomyValueId) {
                                    taxonomyValue.value = taxonomyValue.value.split(TaxonomyService.taxonomyValueIdStub).join(submitResult.taxonomyValueId);
                                    taxonomyValue.displayValue = taxonomyValue.displayValue.split(TaxonomyService.taxonomyValueIdStub).join(submitResult.taxonomyValueId);

                                    taxonomyValue.setStatus(TaxonomyStatus.Submitted, 'Submitted');
                                }
                            } catch (error) {
                                taxonomyValue.setStatus(TaxonomyStatus.SubmittingError, this.appHelper.getErrorMessage(error));
                            }
                        }
                    }

                    this.setChanges(sheet, taxonomyId, rowsToProcess);
                    await context.sync();
                }
            }
        });
    }

    //#endregion

    //#region Proxy for Excel cache

    taxonomyExists(taxonomyId: number): boolean {
        return this.excelService?.taxonomyExists(taxonomyId) === true;
    }

    fieldExists(fieldId: number): boolean {
        return this.excelService?.fieldExists(fieldId) === true;
    }

    dropDownItemsExist(fieldId: number): boolean {
        return this.excelService?.dropDownItemsExist(fieldId) === true;
    }

    childDropDownItemsExist(fieldId: number, itemId: string): boolean {
        return this.excelService?.childDropDownItemsExist(fieldId, itemId) === true;
    }

    mdmDropDownItemsExist(taxonomyId: number, fieldId: number): boolean {
        return this.excelService?.mdmDropDownItemsExist(taxonomyId, fieldId) === true;
    }

    childMdmDropDownItemsExist(taxonomyId: number, fieldId: number, itemId: string): boolean {
        return this.excelService?.childMdmDropDownItemsExist(taxonomyId, fieldId, itemId) === true;
    }

    taxonomyValuesExist(taxonomyId: number): boolean {
        return this.excelService?.taxonomyValuesExist(taxonomyId) === true;
    }

    getDropDownItems(fieldId: number): DropDownItem[] {
        return this.excelService?.getDropDownItems(fieldId);
    }

    getChildDropDownItems(fieldId: number, itemId: string): ChildDropDownItem[] {
        return this.excelService?.getChildDropDownItems(fieldId, itemId);
    }

    getMdmDropDownItems(taxonomyId: number, fieldId: number): DropDownItem[] {
        return this.excelService?.getMdmDropDownItems(taxonomyId, fieldId);
    }

    getChildMdmDropDownItems(taxonomyId: number, fieldId: number, itemId: string): ChildDropDownItem[] {
        return this.excelService?.getChildMdmDropDownItems(taxonomyId, fieldId, itemId);
    }

    getTaxonomyValues(taxonomyId: number): DropDownItem[] {
        return this.excelService?.getTaxonomyValues(taxonomyId);
    }

    //#endregion

    //#region Cascading

    private applyCascading(row: TaxonomyRow): void {
        const cellsCache = row.cells
            .filter(c =>
                c.field.type === FieldType.Dropdown ||
                c.field.type === FieldType.MDMData)
            .map(c => new CellCascadingCache(c));

        do {
            for (const cellCache of cellsCache) { // cache reset
                cellCache.reset();
            }

            this.fillParentCells(cellsCache);
            this.fillChildCells(cellsCache);

            if (this.validateCascading(cellsCache)) { // dependencies are valid
                this.setCellChanges(cellsCache);
                break;
            }
        } while (true);
    }

    private fillParentCells(cellsCache: CellCascadingCache[]): void {
        for (const cellCache of cellsCache) {
            for (const anotherCellCache of cellsCache.filter(c => c !== cellCache)) {
                const anotherCell = anotherCellCache.cell;
                const selectedItem = anotherCellCache.value
                    ? this.appHelper.find(anotherCell.items, i => i.name == anotherCellCache.value)
                    : undefined;

                if (selectedItem) {
                    const filter = selectedItem.childItems
                        .filter(c => c.fieldId === cellCache.cell.field.id)
                        .map(c => this.appHelper.find(cellCache.cell.items, i => i.id == c.id))
                        .filter(i => i);

                    if (filter.length > 0) {
                        cellCache.parentCells.push({ cell: anotherCell, filter: filter });
                    }
                }
            }
        }
    }

    private fillChildCells(cellsCache: CellCascadingCache[]): void {
        for (const cellCache of cellsCache) {
            const selectedItem = cellCache.value
                ? this.appHelper.find(cellCache.cell.items, i => i.name == cellCache.value)
                : undefined;

            if (selectedItem) {
                const childGroups = this.groupBy(selectedItem.childItems, 'fieldId');

                for (const childFieldId of cellCache.allChildFieldIds) {
                    const childCellCache = this.appHelper.find(cellsCache, c => c.cell.field.id === childFieldId);

                    if (childCellCache) { // if there is such a field in the table
                        const childItems: DropDownItem[] = [];
                        const childFieldItems = childGroups[childFieldId.toString()];

                        if (childFieldItems) {
                            for (const child of childFieldItems) {
                                const childItem = this.appHelper.find(childCellCache.cell.allItems, i => i.id == child.id);

                                if (childItem) {
                                    childItems.push(childItem);
                                }
                            }
                        }

                        cellCache.childCells.push({ cell: childCellCache.cell, filter: childItems });
                    }
                }
            }
        }
    }

    private validateCascading(cellsCache: CellCascadingCache[]): boolean {
        const toProcess = cellsCache
            //.filter(c => c.parentCells.length > 0) // ?
            .sort((a, b) => a.parentCells.length - b.parentCells.length); // ASC (the least dependent first)
        // Should the second step of sorting dependent on the isAffected state be added?

        for (const cellCache of toProcess) {
            for (const child of cellCache.childCells) {
                const childCellCache = this.appHelper.find(cellsCache, c => c.cell === child.cell);

                // Hidden fields participate in cascading as parents (their selected
                // value filters dependent dropdowns) but their own value and items
                // must never be reset/filtered by downstream cascading. This prevents
                // a previously selected hidden parent from being cleared when the
                // user later picks a value in its dependent dropdown.
                if (childCellCache.cell.field.hidden) {
                    continue;
                }

                if (childCellCache.value && !this.appHelper.find(child.filter, i => i.name == childCellCache.value)) {
                    childCellCache.value = '';
                    return false;
                } else {
                    const childCellItems = childCellCache.items;

                    for (let i = 0; i < childCellItems.length;) {
                        if (!this.appHelper.includes(child.filter, childCellItems[i])) {
                            childCellItems.splice(i, 1);
                        } else {
                            i++;
                        }
                    }
                }
            }
        }

        return true;
    }

    private setCellChanges(cellsCache: CellCascadingCache[]): void {
        for (const cellCache of cellsCache) {
            const cell = cellCache.cell;

            if (cell.valueChanged = cellCache.value != cell.value) {
                cell.value = cellCache.value;
            }

            if (cell.itemsChanged = cellCache.items.length !== cell.items.length ||
                cellCache.items.some(i => !this.appHelper.includes(cell.items, i))) {
                cell.items = cellCache.items;
            }
        }
    };

    //#endregion

//#region Taxonomy values

    private applyTaxonomyValue(row: TaxonomyRow): void {
        const taxonomyValue = row.taxonomyValue;

        if (taxonomyValue.lastStatus !== TaxonomyStatus.Submitted && row.cells.length > 0) {
            const emptyCells = row.cells.filter(c => !c.value);

            if (emptyCells.length === row.cells.length) { // there are no entered data
                taxonomyValue.setStatus(TaxonomyStatus.Empty, '');
            } else if (taxonomyValue.status.value < TaxonomyStatus.Warning) {
                for (let c = 0; c < emptyCells.length; c++) {
                    const cell = emptyCells[c];

                    if (cell.range && cell.field.mandatory) {
                        taxonomyValue.setStatus(TaxonomyStatus.Warning,
                            `The '${cell.field.name}' value is mandatory`);
                        break;
                    }
                }

                for (let c = 0; c < row.cells.length && taxonomyValue.status.value < TaxonomyStatus.Error; c++) {
                    const cell = row.cells[c];

                    if (cell.value) {
                        switch (cell.field.type) {
                            case FieldType.Dropdown:
                            case FieldType.MDMData:
                            case FieldType.MDMSearch:
                            case FieldType.Taxonomy:
                                const item = this.appHelper.find(cell.items, i => i.name == cell.value);
                                if (!cell.field.hidden) {
                                    if (!item) {
                                        taxonomyValue.setStatus(TaxonomyStatus.Error,
                                            `The '${cell.field.name}' value is not in the list`);
                                    } else if (!item.code) {
                                        taxonomyValue.setStatus(TaxonomyStatus.Error,
                                            `The '${cell.field.name}' value has no associated code. Please contact the Taxonomy support`);
                                    }
                                    else {
                                        if(item.taxonomy_value_id) {

                                        if (cell.field.displayCode === 'I') {
                                            taxonomyValue.addValueChunk(item.taxonomy_value_id, item.taxonomy_value_id);
                                        }
                                        else if (cell.field.displayCode === 'C') {
                                            taxonomyValue.addValueChunk(item.code, item.code);
                                        }
                                        else if (cell.field.displayCode === 'U') {
                                            taxonomyValue.addValueChunk(item.name, item.name);
                                        }                                       
                                        }
                                    else {
                                            taxonomyValue.addValueChunk(item.code, item.name);
                                        }
                                    }
                                }
                                else {
                                    continue; // skip hidden dropdown fields
                                }
                                break;
                            case FieldType.Text:
                                if (cell.field.characterLimit > 0 && cell.value.length > cell.field.characterLimit) {
                                    taxonomyValue.setStatus(TaxonomyStatus.Error,
                                        `The '${cell.field.name}' value is too long`);
                                } else {
                                    const delimiter = cell.field.delimiter;

                                    if (delimiter) {
                                        const replacement = delimiter === '-' ? '_' : '-';
                                        taxonomyValue.addValueChunk(cell.value.split(delimiter).join(replacement));
                                    } else {
                                        taxonomyValue.addValueChunk(cell.value);
                                    }
                                }
                                break;
                            case FieldType.Date:
                                const dateNumber = +cell.value;

                                if (isNaN(dateNumber)) {
                                    taxonomyValue.setStatus(TaxonomyStatus.Error,
                                        `The '${cell.field.name}' value is not a valid date`);
                                } else {
                                    const date = new Date((dateNumber - (25567 + 2)) * 86400 * 1000); // https://stackoverflow.com/questions/37803568/how-to-handle-date-type-in-excel-addin-office-js
                                    const dateChunk = this.appHelper.formatDate(date, cell.field.dateFormat);

                                    taxonomyValue.addValueChunk(dateChunk);
                                }
                                break;
                            case FieldType.Static:
                                if (cell.field.staticValue) {
                                    taxonomyValue.addValueChunk(cell.field.staticValue);
                                }
                                break;
                            default:
                                throw new Error(`Unknown field type: ${cell.field.type}.`);
                        }
                    } else if (cell.field.type === FieldType.TaxonomyId) {
                        taxonomyValue.addValueChunk(TaxonomyService.taxonomyValueIdStub);
                    }

                    // Skip delimiter for:
                    //  - hidden fields (they contribute nothing to the taxonomy value)
                    //  - empty cells that didn't produce any value chunk (so we don't
                    //    output stray delimiters for fields the user hasn't filled in)
                    const needsDelimiter = (cell.value && !cell.field.hidden) ||
                        cell.field.type === FieldType.TaxonomyId;

                    if (c < row.cells.length - 1 && needsDelimiter) {
                        taxonomyValue.addValueChunk(cell.field.delimiter);
                    }
                }
            }
        }
    }

    //#endregion

    //#region Helpers

    private setChanges(sheet: Excel.Worksheet, taxonomyId: number, rows: TaxonomyRow[]): void {
        if (rows.length > 0) {
            const cached = this.processedKeys[taxonomyId];
            const key = this.getRangeToCache(rows[0]).toString();
            const taxonomyValueColumnIndex = rows[0].taxonomyValue.valueRange.columnIndex;

            if (!cached) {
                this.processedKeys[taxonomyId] = {
                    taxonomyValueColumnIndex: taxonomyValueColumnIndex,
                    lastKey: key
                };
            } else {
                cached.lastKey = key;
                cached.taxonomyValueColumnIndex = taxonomyValueColumnIndex;
            }

            this.excelService.setChanges(sheet, taxonomyId, rows);
        }
    }

    private getRangeToCache(row: TaxonomyRow): Range {
        for (const cell of row.cells) {
            if (cell.valueChanged) {
                return cell.range;
            }
        }

        return row.taxonomyValue.valueRange;
    }

    private groupBy<T>(arr: T[], key: string): { [key: string]: T[]; } {
        return arr.reduce((prev, curr) => {
            (prev[curr[key]] = prev[curr[key]] || []).push(curr);
            return prev;
        }, {});
    };

    //#endregion
}